import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Table } from '@els/els-react--table';
import RcsCheckboxNew from '@Pages/Results/Containers/RcsCheckboxContainer';
import InnerHTML from '@Components/InnerHTML';
import ShowMore from '@Components/Results/utils/ShowMore';
import {
  COMMERCIAL_DETAILS_SHOW_MORE_THRESHOLD,
  DEFAULT_DETAIL_SORT_STATE,
  PRODUCT_OFFERS_TABLE_CAPTION,
  SUPPLIER_DETAILS_PREVIEW_LIMIT,
} from './constants';
import { buildNormalPreviewRows, formatPriceValueHtml, hasSinglePriceCurrency } from './SubstanceCommercialSupplierInfo.helpers';
import {
  buildNormalDetailTableColumns,
  getOfferStackItemKey,
  getVisibleOfferStackItems,
  prepareDetailRowForLeydenTable,
  useDetailSortState,
} from './SubstanceCommercialSupplierDetailTable.helpers';
import SubstanceCommercialSupplierDetailTableHead from './SubstanceCommercialSupplierDetailTableHead';

import './SubstanceCommercialSupplierDetailTables.scss';

function SubstanceCommercialSupplierNormalOffersTable({
  columns,
  isProdSelectionEnabled = false,
  offers,
  substanceId,
  substanceIndex,
  tableId,
}) {
  const { sortState, handleSort } = useDetailSortState();
  const [visibleOfferCount, setVisibleOfferCount] = useState(SUPPLIER_DETAILS_PREVIEW_LIMIT);
  const [sortAnnouncement, setSortAnnouncement] = useState('');
  const isPriceSortable = hasSinglePriceCurrency(offers);
  const effectiveSortState = sortState.field === 'price' && !isPriceSortable ? DEFAULT_DETAIL_SORT_STATE : sortState;
  const tableColumns = useMemo(() => buildNormalDetailTableColumns(columns, isProdSelectionEnabled), [columns, isProdSelectionEnabled]);
  const previewRows = useMemo(
    () => buildNormalPreviewRows(offers, visibleOfferCount, effectiveSortState),
    [offers, visibleOfferCount, effectiveSortState]
  );
  const tableRows = useMemo(() => previewRows.map(prepareDetailRowForLeydenTable), [previewRows]);

  return (
    <div className="rcs-commercial-details-table-wrapper">
      <span className="sr-only" role="status" aria-live="polite" aria-atomic="true">
        {sortAnnouncement}
      </span>
      <Table
        a11y={{
          name: 'Product offers',
          description: PRODUCT_OFFERS_TABLE_CAPTION,
        }}
        className="rx-table rx-table--full-width rcs-commercial-details-table"
        columns={tableColumns}
        data={tableRows}
        id={`${tableId}-normal-offers`}
        rowKey="key"
        subtleDividers
        useSemantic
        verticalDividers
        renderHead={() => (
          <SubstanceCommercialSupplierDetailTableHead
            tableColumns={tableColumns}
            handleSort={handleSort}
            isPriceSortable={isPriceSortable}
            onSortAnnouncement={setSortAnnouncement}
            priceSortTooltipId={`${tableId}-normal-offers-price-sort-tooltip`}
            sortState={sortState}
          />
        )}
        renderDatum={(rows = []) =>
          rows.map((previewRow) => {
            const stackOffers = getVisibleOfferStackItems(previewRow?.offers);

            return (
              <tr key={previewRow.key} className="c-els-table__row no-hover-background">
                {isProdSelectionEnabled && (
                  <td className="c-els-table__cell c-els-table__cell--stacked">
                    <div className="rcs-commercial-details-table__stack">
                      {stackOffers.map((offer, offerIndex) => (
                        <div
                          key={getOfferStackItemKey(previewRow.key, offerIndex, 'checkbox')}
                          className="rcs-commercial-details-table__stack-item">
                          <RcsCheckboxNew
                            substanceId={substanceId}
                            substanceIndex={substanceIndex}
                            productId={offer.rcSubstanceId}
                            productIndex={offer.index}
                          />
                        </div>
                      ))}
                    </div>
                  </td>
                )}
                {columns.showPurity && (
                  <td className="column-purity c-els-table__cell">
                    <InnerHTML tag="span" html={`${previewRow.purityValue || ''}`} />
                  </td>
                )}
                {columns.showPackageSize && (
                  <td className="column-size c-els-table__cell c-els-table__cell--stacked">
                    <div className="rcs-commercial-details-table__stack">
                      {stackOffers.map((offer, offerIndex) => (
                        <InnerHTML
                          tag="div"
                          key={getOfferStackItemKey(previewRow.key, offerIndex, 'package')}
                          className="rcs-commercial-details-table__stack-item"
                          html={`${offer.packageSize || ''}`}
                        />
                      ))}
                    </div>
                  </td>
                )}
                {columns.showPrice && (
                  <td className="column-price c-els-table__cell c-els-table__cell--stacked">
                    <div className="rcs-commercial-details-table__stack">
                      {stackOffers.map((offer, offerIndex) => (
                        <InnerHTML
                          tag="div"
                          key={getOfferStackItemKey(previewRow.key, offerIndex, 'price')}
                          className="rcs-commercial-details-table__stack-item"
                          html={formatPriceValueHtml(offer)}
                        />
                      ))}
                    </div>
                  </td>
                )}
                {columns.showShipmentCountry && (
                  <td className="c-els-table__cell">
                    <InnerHTML tag="span" html={`${previewRow.shipmentCountryValue || ''}`} />
                  </td>
                )}
                {columns.showShipping && (
                  <td className="c-els-table__cell">
                    <InnerHTML tag="span" html={`${previewRow.shippingValue || ''}`} />
                  </td>
                )}
                {columns.showAvailability && <td className="c-els-table__cell">{previewRow.availabilityValue || ''}</td>}
              </tr>
            );
          })
        }
      />

      {offers.length > SUPPLIER_DETAILS_PREVIEW_LIMIT && (
        <div className="rcs-commercial-details-table__footer u-els-padding-top-1o4">
          <ShowMore
            hideIcon
            preventFocusScroll
            idSuffix={`${tableId}-products`}
            container=".rcs-commercial-detail-panel"
            threshold={COMMERCIAL_DETAILS_SHOW_MORE_THRESHOLD}
            initial={SUPPLIER_DETAILS_PREVIEW_LIMIT}
            current={Math.min(visibleOfferCount, offers.length)}
            total={offers.length}
            moreHandler={({ lastItem }) => setVisibleOfferCount(lastItem)}
            lessHandler={() => setVisibleOfferCount(SUPPLIER_DETAILS_PREVIEW_LIMIT)}
          />
        </div>
      )}
    </div>
  );
}

SubstanceCommercialSupplierNormalOffersTable.propTypes = {
  columns: PropTypes.shape({
    showPurity: PropTypes.bool,
    showPackageSize: PropTypes.bool,
    showPrice: PropTypes.bool,
    showShipping: PropTypes.bool,
    showShipmentCountry: PropTypes.bool,
    showAvailability: PropTypes.bool,
  }).isRequired,
  isProdSelectionEnabled: PropTypes.bool,
  offers: PropTypes.arrayOf(PropTypes.object).isRequired,
  substanceId: PropTypes.string.isRequired,
  substanceIndex: PropTypes.number.isRequired,
  tableId: PropTypes.string.isRequired,
};

export default SubstanceCommercialSupplierNormalOffersTable;
